import { Trans } from "react-i18next";
import React from "react";
import { Badge, Button, Card, Form, Spinner } from "react-bootstrap";
import "./SettingsWorkbenchLayout.css";

export interface SettingsWorkbenchItem {
  id: string;
  title: string;
  subtitle?: string;
  badge?: string;
  locked?: boolean;
}

interface SettingsWorkbenchLayoutProps {
  title: string;
  description?: string;
  items: SettingsWorkbenchItem[];
  selectedItemId?: string;
  searchValue?: string;
  searchPlaceholder?: string;
  listTitle?: string;
  loading?: boolean;
  emptyMessage?: string;
  actions?: React.ReactNode;
  footerActions?: React.ReactNode;
  children: React.ReactNode;
  onSearchChange?: (value: string) => void;
  onSelectItem?: (item: SettingsWorkbenchItem) => void;
}

const SettingsWorkbenchLayout: React.FC<SettingsWorkbenchLayoutProps> = ({
  title,
  description,
  items,
  selectedItemId,
  searchValue = "",
  searchPlaceholder = "Search",
  listTitle = "Records",
  loading = false,
  emptyMessage = "No records found.",
  actions,
  footerActions,
  children,
  onSearchChange,
  onSelectItem,
}) => {
  return (
    <div className="settings-workbench app-ui-surface">
      <header className="settings-workbench__header">
        <div>
          <h1>{title}</h1>
          {description && <p>{description}</p>}
        </div>
        {actions && <div className="settings-workbench__actions">{actions}</div>}
      </header>

      <div className="settings-workbench__body">
        <Card className="settings-workbench__list-card">
          <Card.Header className="settings-workbench__list-header">
            <strong>{listTitle}</strong>
            <Badge bg="secondary">{items.length}</Badge>
          </Card.Header>
          <Card.Body className="p-0">
            {onSearchChange && (
              <div className="settings-workbench__search">
                <Form.Control
                  value={searchValue}
                  onChange={(event) => onSearchChange(event.target.value)}
                  placeholder={searchPlaceholder}
                  aria-label={searchPlaceholder}
                />
              </div>
            )}

            {loading ? (
              <div className="settings-workbench__loading">
                <Spinner animation="border" size="sm" />
                <span><Trans ns="features" i18nKey="ui.copy.b04ba49f8486" /></span>
              </div>
            ) : items.length === 0 ? (
              <div className="settings-workbench__empty">{emptyMessage}</div>
            ) : (
              <div
                className="settings-workbench__list"
                role="listbox"
                aria-label={listTitle}
              >
                {items.map((item) => {
                  const selected = item.id === selectedItemId;
                  return (
                    <Button
                      key={item.id}
                      type="button"
                      variant="link"
                      className={`settings-workbench__list-item ${
                        selected ? "is-selected" : ""
                      }`}
                      role="option"
                      aria-selected={selected}
                      aria-label={item.title}
                      onClick={() => onSelectItem?.(item)}
                    >
                      <span className="settings-workbench__item-main">
                        <span className="settings-workbench__item-title">
                          {item.title}
                        </span>
                        {item.subtitle && (
                          <span className="settings-workbench__item-subtitle">
                            {item.subtitle}
                          </span>
                        )}
                      </span>
                      <span className="settings-workbench__item-meta">
                        {item.badge && (
                          <Badge bg={item.locked ? "secondary" : "primary"}>
                            {item.badge}
                          </Badge>
                        )}
                      </span>
                    </Button>
                  );
                })}
              </div>
            )}
          </Card.Body>
          {footerActions && (
            <Card.Footer className="settings-workbench__footer">
              {footerActions}
            </Card.Footer>
          )}
        </Card>

        <div className="settings-workbench__detail">{children}</div>
      </div>
    </div>
  );
};

export default SettingsWorkbenchLayout;
